import { writable, derived, get } from 'svelte/store';
import { COMPANY } from './config.js';
import { readJson, readSession } from './storage.js';
import { validateStock } from './api.js';
const DRAFT_KEY = 'vendeySvelteDraft:' + COMPANY;
const savedDraft = readJson(DRAFT_KEY, {});
const initialSession = readSession();
export const sessionIdentity = session => session ? `${session.sellerId}:${session.warehouseId}:${session.boxId}` : '';
const draft = savedDraft.owner === sessionIdentity(initialSession) && initialSession ? savedDraft : {};
export const cart = writable(Array.isArray(draft.items) ? draft.items : []);
export const customer = writable(draft.customer || { document: '88888888', name: 'CLIENTES VARIOS', type: '01', phone: '', address: '' });
export const emissionBusy = writable(false);
export const totals = derived(cart, items => {
  const total = Math.round(items.reduce((sum, item) => sum + item.quantity * item.price, 0) * 100) / 100;
  return { subtotal: total / 1.18, tax: total - total / 1.18, total };
});
let persistenceEnabled = false;
export function startDraftPersistence() {
  if (persistenceEnabled) return;
  persistenceEnabled = true;
  derived([cart, customer], ([items, customer]) => ({ items, customer })).subscribe(value => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...value, owner: sessionIdentity(readSession()) })); } catch { /* La venta sigue disponible en memoria. */ }
  });
}
export function normalizeProduct(product) {
  const id = String(product.NCODPRODUCTO ?? product.ncodproducto ?? product.id ?? '');
  const price = Number(product.NPRECIO ?? product.NPRECIOVENTA ?? product.price ?? 0);
  if (!id || !Number.isFinite(price) || price < 0) throw new Error('El producto no tiene código o precio válido.');
  return { id, name: String(product.CPRODUCTODESC ?? product.CDESCRIPCION ?? product.name ?? 'Producto'), price,
    stock: Number(product.NSTOCKACTUAL ?? product.stock ?? 0), controlled: product.controlled ?? true,
    baseId: id, factor: 1, ...product, id, price };
}
export function assertStock(items) {
  for (const item of items.filter(item => item.controlled)) {
    if (!Number.isFinite(item.factor) || item.factor <= 0) throw new Error(`Factor de conversión inválido para ${item.name}.`);
    if (!Number.isFinite(item.stock) || item.quantity > item.stock) throw new Error(`Stock insuficiente para ${item.name}. Disponible: ${item.stock}.`);
    if (Number.isFinite(item.baseStock)) {
      const consumption = items.filter(other => other.controlled && other.baseId === item.baseId).reduce((sum, other) => sum + other.quantity * other.factor, 0);
      if (consumption > item.baseStock + 0.000001) throw new Error(`Las presentaciones de ${item.name} superan el stock del producto base.`);
    }
  }
}
export async function addProduct(product, session) {
  if (readJson('vendeySveltePending:' + COMPANY)) throw new Error('Resuelve la emisión pendiente antes de agregar productos.');
  if (get(emissionBusy)) throw new Error('Espera a que termine la emisión.');
  let next = normalizeProduct(product);
  next.priceListId = session.priceListId || 1;
  const controlled = localStorage.getItem('validarStock') !== 'false';
  next = controlled ? { ...next, ...await validateStock(next.id, session) } : { ...next, controlled: false };
  if (get(emissionBusy)) throw new Error('Espera a que termine la emisión.');
  cart.update(items => {
    const found = items.find(item => item.id === next.id && item.price === next.price);
    const candidate = found ? items.map(item => item === found ? { ...item, ...next, quantity: item.quantity + 1 } : item) : [...items, { ...next, lineId: crypto.randomUUID(), quantity: 1 }];
    assertStock(candidate); return candidate;
  });
}
export function updateLine(id, fields) {
  if (get(emissionBusy)) return;
  cart.update(items => {
    const next = items.map(item => item.lineId === id ? { ...item, ...fields } : item).filter(item => item.quantity !== 0);
    if (next.some(item => !Number.isFinite(item.quantity) || item.quantity < 0 || !Number.isFinite(item.price) || item.price < 0)) throw new Error('Cantidad y precio deben ser números válidos.');
    assertStock(next); return next;
  });
}
export function setQuantity(id, quantity) { updateLine(id, { quantity: Number(quantity) }); }
export function clearSale() { if (!get(emissionBusy)) cart.set([]); }
